import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import AuthFormLayout from './common/AuthFormLayout';

const ForgotPasswordScreen = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [resetUrl, setResetUrl] = useState(null);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post('/api/auth/forgot-password', { email });
      setIsSubmitted(true);
      if (response.data.reset_url) {
        setResetUrl(response.data.reset_url);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError(
          'No account found with this email address. Please check your email or create a new account.'
        );
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.request) {
        setError('Unable to connect to server. Please try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <AuthFormLayout
        headline={resetUrl ? 'Password reset link ready' : 'Check your email'}
        subhead={
          resetUrl
            ? 'Email sending is not configured. Use this reset link:'
            : `We sent password reset instructions to ${email}.`
        }
      >
        {resetUrl ? (
          <div className="surface-panel p-4 mb-4 break-all">
            <a href={resetUrl} className="text-body text-pine hover:text-pine-deep">
              {resetUrl}
            </a>
            <p className="text-label text-secondary mt-3">This link will expire in 1 hour.</p>
          </div>
        ) : (
          <div className="surface-panel p-4 mb-6">
            <h3 className="font-display text-h3 text-primary mb-2">What happens next</h3>
            <ul className="text-body text-secondary space-y-2 list-disc pl-5">
              <li>Check your email inbox and spam folder</li>
              <li>Click the reset link in the email</li>
              <li>The link expires in 1 hour for security</li>
              <li>Create your new password</li>
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => {
              setIsSubmitted(false);
              setEmail('');
              setError('');
              setResetUrl(null);
            }}
            className="btn-secondary w-full"
          >
            Send to a different email
          </button>
          <Link to="/auth" className="btn-ghost w-full text-center">
            Back to sign in
          </Link>
        </div>

        {!resetUrl && (
          <p className="text-center text-body text-secondary mt-6">
            Did not receive the email?{' '}
            <button
              type="button"
              onClick={() => handleSubmit({ preventDefault: () => {} })}
              className="text-pine hover:text-pine-deep"
              disabled={isLoading}
            >
              Resend reset email
            </button>
          </p>
        )}
      </AuthFormLayout>
    );
  }

  return (
    <AuthFormLayout
      headline="Forgot password?"
      subhead="Enter your email and we will send reset instructions."
    >
      {error && (
        <div
          className={`mb-4 p-4 rounded-md border text-body ${
            error.includes('No account found')
              ? 'border-warning bg-bone-raised text-warning'
              : 'border-danger bg-bone-raised text-danger'
          }`}
          role="alert"
        >
          {error}
          {error.includes('No account found') && (
            <p className="mt-2">
              <Link to="/auth" className="text-pine hover:text-pine-deep">
                Create a new account instead
              </Link>
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="block text-label text-secondary mb-2">
            Email address
          </label>
          <div className="relative">
            <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-editorial pl-10"
              placeholder="you@example.com"
              autoComplete="email"
              disabled={isLoading}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading || !email || !validateEmail(email)}
          className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-5 h-5 border-2 border-bone border-t-transparent rounded-full animate-spin" />
              Checking email…
            </span>
          ) : (
            'Send reset instructions'
          )}
        </button>
      </form>

      <p className="text-center">
        <Link to="/auth" className="text-body text-pine hover:text-pine-deep">
          Back to sign in
        </Link>
      </p>

      <p className="text-center text-body text-secondary">
        Do not have an account?{' '}
        <Link to="/auth" className="text-pine hover:text-pine-deep">
          Sign up here
        </Link>
      </p>
    </AuthFormLayout>
  );
};

export default ForgotPasswordScreen;
